import styled from "styled-components";
import * as React from "react";
import { Nav, Props as NavProps } from "src/modules/Nav";
import { useIsoContext } from "../isoContext";
import classNames from "classnames";

/** Matches `NavHeader` height — mobile top inset for portaled layers and related layout. */
export const MOBILE_STICKY_HEADER_PX = 44;
/** Narrow desktop nav rail — portaled layers start to the right of this. */
export const DESKTOP_NAV_RAIL_WIDTH_PX = 44;

/** Targets `createPortal` for layers that should sit in the main column (fixed + rail/header offsets). */
export const PAGE_LAYER_PORTAL_ID = "page-layer-portal";

interface Props {
  action?: NavProps["action"];
  header?: React.ReactNode;
  maxWidth?: number;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  scrollable?: boolean;
}

export function Page({
  action,
  header,
  maxWidth,
  children,
  style,
  fullWidth = false,
  scrollable = true,
}: Props) {
  const { isLarge } = useIsoContext();
  return (
    <Layout className={classNames({ "is-desktop": isLarge })}>
      <Nav action={action} />
      <Main $scrollable={scrollable}>
        {header}
        <Scroll $scrollable={scrollable}>
          <PagePortalMount id={PAGE_LAYER_PORTAL_ID} $desktop={isLarge} $maxWidth={maxWidth} />

          <InnerPage
            $maxWidth={maxWidth}
            $fullWidth={fullWidth}
            $scrollable={scrollable}
            style={style}
          >
            {children}
          </InnerPage>
        </Scroll>
      </Main>
    </Layout>
  );
}

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  &.is-desktop {
    flex-direction: row;
  }
`;

const Main = styled.div<{ $scrollable: boolean }>`
  flex-grow: 1;
  background: var(--bg-color);
  overflow: ${({ $scrollable }) => ($scrollable ? "auto" : "hidden")};
  display: flex;
  flex-direction: column;
`;

const Scroll = styled.div<{ $scrollable: boolean }>`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  ${({ $scrollable }) => ($scrollable ? "" : "overflow: hidden;")}
`;

const InnerPage = styled.div<{
  $maxWidth?: number;
  $fullWidth: boolean;
  $scrollable: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${({ $fullWidth }) => ($fullWidth ? "0" : "0 10px")};
  ${({ $scrollable }) => ($scrollable ? "" : "overflow: hidden;")}
  @media (min-width: 600px) {
    padding: 20px;
  }
  ${({ $maxWidth }) => ($maxWidth ? `max-width: ${$maxWidth}px;` : "")}
`;

/**
 * Fixed band aligned with `InnerPage`: same horizontal padding; on desktop, inset past the nav rail.
 * When `maxWidth` is set (e.g. 600), width matches that column instead of spanning the full main area.
 */
const PagePortalMount = styled.div<{ $desktop: boolean; $maxWidth?: number }>`
  position: fixed;
  z-index: 9;
  top: ${({ $desktop }) => ($desktop ? 0 : MOBILE_STICKY_HEADER_PX)}px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  box-sizing: border-box;
  pointer-events: none;
  height: 0;
  overflow: visible;
  @media (min-width: 600px) {
    transform: translateX(20px);
  }
  width: ${({ $desktop }) => ($desktop ? "560px" : "100%")};
  & > * {
    pointer-events: auto;
  }
`;
